import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Play, Pause, Music4 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Short song clips, so a booker can hear the band in a few seconds.
 *
 * One shared <audio> element rather than one per row: several players running
 * at once is the usual failure of a list like this, and a single element makes
 * "only one thing plays" the default rather than something to police.
 */
export const TrackClips = () => {
  const { data: tracks = [] } = useQuery({
    queryKey: ["tracks"],
    queryFn: async () => (await axios.get(`${API_BASE}/tracks`)).data ?? [],
  });

  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  // Never leave audio playing behind us when the section unmounts.
  useEffect(() => {
    const el = audioRef.current;
    return () => {
      if (el) {
        el.pause();
        el.src = "";
      }
    };
  }, []);

  const toggle = (track) => {
    const el = audioRef.current;
    if (!el) return;

    if (playingId === track.id) {
      el.pause();
      setPlayingId(null);
      return;
    }
    setLoadingId(track.id);
    el.src = track.audio_url;
    el.play().then(
      () => {
        setPlayingId(track.id);
        setLoadingId(null);
      },
      () => {
        // Autoplay blocked, bad file, or offline — say nothing loudly, just reset.
        setPlayingId(null);
        setLoadingId(null);
      }
    );
  };

  if (tracks.length === 0) return null;

  return (
    <div className="mt-12" data-testid="track-clips">
      <div className="flex items-center gap-3 mb-5">
        <Music4 size={16} className="text-[#DC2626]" />
        <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
          Have a listen
        </span>
      </div>

      <ul className="border-t border-white/10">
        {tracks.map((t, i) => {
          const isPlaying = playingId === t.id;
          const isLoading = loadingId === t.id;
          return (
            <motion.li
              key={t.id}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              data-testid={`track-${t.id}`}
              className="border-b border-white/10"
            >
              <button
                onClick={() => toggle(t)}
                aria-label={isPlaying ? `Stop ${t.title}` : `Play a clip of ${t.title}`}
                data-testid={`track-play-${t.id}`}
                className="w-full flex items-center gap-4 py-4 text-left group"
              >
                <span
                  className={`flex items-center justify-center w-11 h-11 flex-shrink-0 border transition-colors ${
                    isPlaying
                      ? "bg-[#B91C1C] border-[#B91C1C] text-white"
                      : "border-white/25 text-white/80 group-hover:border-[#B91C1C] group-hover:bg-[#B91C1C] group-hover:text-white"
                  }`}
                >
                  {isPlaying ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-oswald uppercase tracking-wide text-white text-lg truncate">
                    {t.title}
                  </span>
                  {t.note && (
                    <span className="block text-white/50 text-sm font-manrope truncate">{t.note}</span>
                  )}
                </span>
                <span className="font-oswald uppercase tracking-[0.18em] text-[10px] text-white/40 flex-shrink-0">
                  {isLoading ? "Loading" : isPlaying ? "Playing" : "Clip"}
                </span>
              </button>
            </motion.li>
          );
        })}
      </ul>

      <p className="text-white/40 text-xs font-manrope mt-4">
        Short samples. Full songs are on Spotify.
      </p>

      <audio
        ref={audioRef}
        preload="none"
        onEnded={() => setPlayingId(null)}
        onError={() => {
          setPlayingId(null);
          setLoadingId(null);
        }}
      />
    </div>
  );
};
